import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PropertyKitEntry, isEntryMissing } from '@/hooks/inventory';
import { usePropertyArticles } from '@/hooks/inventory/usePropertyArticles';
import EntryStatusBadge from '../components/EntryStatusBadge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getThumbnailImageUrl } from '@/lib/cloudinary';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PropertyArticlesTableProps {
  entries: PropertyKitEntry[];
  propertyId: string;
  onUpdateEntry: (entryId: string) => void;
}

export default function PropertyArticlesTable({ entries, propertyId, onUpdateEntry }: PropertyArticlesTableProps) {
  const { removeEntry, isRemoving } = usePropertyArticles(propertyId);

  const handleRemove = async (entryId: string) => {
    await removeEntry(entryId);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Artículo</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Cantidad</TableHead>
            <TableHead>Última actualización</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => {
            const item = entry.item;
            if (!item) return null;

            const isMissing = isEntryMissing(entry, item);

            return (
              <TableRow key={entry.id} className={isMissing ? 'bg-destructive/5' : ''}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {item.image_url && (
                      <img
                        src={getThumbnailImageUrl(item.image_url)}
                        alt={item.name}
                        className="h-8 w-8 rounded object-cover"
                      />
                    )}
                    <span className="font-medium">{item.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <EntryStatusBadge entry={entry} item={item} />
                </TableCell>
                <TableCell>
                  {item.control_type === 'countable' ? (
                    <span>
                      {entry.current_quantity ?? 0}
                      {entry.ideal_quantity != null && (
                        <span className="text-muted-foreground">/{entry.ideal_quantity}</span>
                      )}
                    </span>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {format(new Date(entry.updated_at), "d MMM yyyy", { locale: es })}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onUpdateEntry(entry.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Quitar artículo?</AlertDialogTitle>
                          <AlertDialogDescription>
                            El artículo "{item.name}" se quitará de esta propiedad.
                            El artículo seguirá disponible en el catálogo.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleRemove(entry.id)}
                            disabled={isRemoving}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {isRemoving ? 'Quitando...' : 'Quitar'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
